import React, { useState, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, ConfirmModal, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { createPackage, updatePackage } from '../services/api';

import { PMPackage } from '../types/pm';
import { notifyDataChanged } from '../utils/dataEvents';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface PackageDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  pkg?: PMPackage | null;
  nextPackageNumber: number;
}

interface FormData {
  name: string;
  amount: number;
  description?: string;
}

const PackageDialog: React.FC<PackageDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  pkg,
  nextPackageNumber,
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm<FormData>();
  const { t } = useTranslation(['translation', 'pages']);
  const amountInput = useNumberInput('integer');
  const [submitting, setSubmitting] = useState(false);

  const isEditMode = !!pkg;

  const actualHandleClose = () => {
    reset();
    onClose(false);
  };

  const {
    handleClose: handleCloseWithWarning,
    showConfirm,
    handleConfirmClose,
    handleCancelClose,
  } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (pkg) {
        reset({
          name: pkg.name,
          amount: pkg.amount,
          description: pkg.description || '',
        });
      } else {
        reset({
          name: t('pmPackages.defaultName', { ns: 'pages', number: nextPackageNumber }) || `第 ${nextPackageNumber} 包`,
          amount: undefined,
          description: '',
        });
      }
    }
  }, [isOpen, pkg, nextPackageNumber, reset, t]);

  // 金額會影響專案總預算，等後端完成重新計算後再關閉並刷新
  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name.trim(),
      amount: Number(data.amount) || 0,
      description: data.description?.trim() || null,
    };

    setSubmitting(true);
    try {
      if (isEditMode) {
        await updatePackage(pkg.id, payload);
        toast.success(t('common.messages.updateSuccess'));
      } else {
        await createPackage(projectId, payload);
        toast.success(t('common.messages.createSuccess'));
      }
      reset();
      onClose(true);
      notifyDataChanged('packages');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || t('common.messages.operationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} closeDisabled={submitting} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmPackages.editTitle', { ns: 'pages' }) || 'Edit Package' : t('pmPackages.addTitle', { ns: 'pages' }) || 'Add Package'} onClose={handleCloseWithWarning} closeDisabled={submitting} closeLabel={t('common.actions.close')} />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <FormFieldWithError
                label={t('pmPackages.fields.name', { ns: 'pages' }) || 'Name'}
                required={true}
                error={errors.name?.message}
                htmlFor="name"
              >
                <Input
                  id="name"
                  {...register('name', {
                    required: t('pmPackages.validation.nameRequired', { ns: 'pages' }) || 'Name is required',
                    maxLength: {
                      value: 200,
                      message: t('pmPackages.validation.nameMaxLength', { ns: 'pages' }) || 'Name must not exceed 200 characters',
                    },
                  })}
                  variant={errors.name ? 'error' : 'default'}
                />
              </FormFieldWithError>

              {/* Amount */}
              <FormFieldWithError
                label={t('pmPackages.fields.amount', { ns: 'pages' }) || 'Quoted Amount'}
                required={true}
                error={errors.amount?.message}
                htmlFor="amount"
              >
                <Input
                  id="amount"
                  type="text"
                  {...amountInput.bind(register('amount', {
                    ...amountInput.validationRules,
                    required: t('pmPackages.validation.amountRequired', { ns: 'pages' }) || 'Amount is required',
                  }))}
                  variant={errors.amount ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>
            </div>

            {/* Description */}
            <FormFieldWithError
              label={t('pmPackages.fields.description', { ns: 'pages' }) || 'Description'}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                {...register('description', {
                  maxLength: {
                    value: 2000,
                    message: t('pmPackages.validation.descriptionMaxLength', { ns: 'pages' }) || 'Description must not exceed 2000 characters',
                  },
                })}
                variant={errors.description ? 'error' : 'default'}
                placeholder={t('pmPackages.placeholders.description', { ns: 'pages' }) || 'Scope of this package'}
                rows={3}
              />
            </FormFieldWithError>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
              disabled={submitting}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <LoadingButton
              type="submit"
              loading={submitting}
              isEditMode={isEditMode}
              customText={{
                create: t('common.actions.create'),
                createLoading: t('common.status.creating'),
                update: t('common.actions.save'),
                updateLoading: t('common.status.saving'),
              }}
              t={t}
            />
          </DialogFooter>
        </form>
    </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default PackageDialog;
